import { Box, styled, useTheme } from '@mui/material';
import classnames from 'classnames';

import {
  CircleIcon,
  ENOPOrganType,
  EResourceType,
  MAX_ORGAN_DOTS_PER_ROW,
  ORGAN_COLORS,
  ORGAN_SHAPES,
} from '#shared';

import { classes } from '../BaseCard';
import { getAvatarBackground, getOrganClassName } from '../utils';

import type { HubReportStatus } from '#entities';

const CountRows = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'isEast' && prop !== 'isSurgeon' && prop !== 'isTopGrowing',
})<{
  isEast: boolean;
  isSurgeon: boolean;
  isTopGrowing: boolean;
}>(({ theme, isEast, isSurgeon, isTopGrowing }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  alignItems: isEast ? 'flex-end' : 'flex-start',
  paddingTop: !isSurgeon && isTopGrowing ? theme.spacing(0) : theme.spacing(1),
  paddingBottom:
    !isSurgeon || !isTopGrowing ? theme.spacing(1) : theme.spacing(0),

  [theme.breakpoints.down('xl')]: {
    gap: 3,
  },

  [theme.breakpoints.down('md')]: {
    gap: 2,
  },

  [theme.breakpoints.down('sm')]: {
    gap: 1,
  },
}));

const IconRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'isTopGrowing' && prop !== 'isSurgeon',
})<{ isTopGrowing: boolean; isSurgeon: boolean }>(
  ({ isTopGrowing, isSurgeon, theme }) => ({
    display: 'flex',
    alignItems: isSurgeon ? 'center' : isTopGrowing ? 'end' : 'start',
    gap: 4,

    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      gap: 1,
    },
  }),
);

// FIXED ORGAN ORDER
const ORGAN_TYPES: ENOPOrganType[] = [
  ENOPOrganType.Liver,
  ENOPOrganType.Heart,
  ENOPOrganType.Lungs,
];

interface StaffWithCountsProps {
  staffInfo: HubReportStatus['staffInfo'][0];
  isEast: boolean;
}

export const StaffWithCounts = ({
  staffInfo,
  isEast,
}: StaffWithCountsProps) => {
  const theme = useTheme();
  const isTopGrowing = staffInfo.teamMemberType === EResourceType.Specialist;
  const isSurgeon = staffInfo.teamMemberType === EResourceType.Surgeon;
  const hasAnyDots = staffInfo?.countsInfo?.some(
    ({ assignedCount = 0, availableCount = 0 }) =>
      assignedCount + availableCount > 0,
  );
  const avatarBackground = getAvatarBackground(
    staffInfo.teamMemberType,
    !!hasAnyDots,
    theme,
  );

  const circleSize = theme.utils.clamp(22, 36);

  // build rows in fixed order, never sorted by counts
  const organRows = ORGAN_TYPES.map(organType => {
    const curCountInfo = staffInfo?.countsInfo?.find(
      info => info?.organType === organType,
    );

    const assignedCount = curCountInfo?.assignedCount ?? 0;
    const availableCount = curCountInfo?.availableCount ?? 0;
    const availableTrialCount = curCountInfo?.availableTrialCount ?? 0;

    const shape = ORGAN_SHAPES[organType];
    const organColor = ORGAN_COLORS[organType];
    const yellowColor = '#FCE96A'; // trials
    const grayColor = theme.palette.grey[700];

    const assignedDots = Array(assignedCount).fill(grayColor);
    const availableRegularDots = Array(
      Math.max(availableCount - availableTrialCount, 0),
    ).fill(organColor);
    const availableTrialDots = Array(availableTrialCount).fill(yellowColor);
    const availableDots = [...availableRegularDots, ...availableTrialDots];

    const dots = isEast
      ? [...assignedDots, ...availableDots]
      : [...availableDots, ...assignedDots];

    let visibleDots = dots.slice(0, MAX_ORGAN_DOTS_PER_ROW);

    // IMPORTANT: keep row even if there are no dots, to preserve layout
    if (visibleDots.length === 0) {
      visibleDots = [null]; // placeholder "invisible" dot
    }

    return {
      organType,
      shape,
      visibleDots,
    };
  });

  return (
    <Box className={classes.staffIcon}>
      <IconRow
        className={classnames(classes.iconRow, {
          [classes.iconRowEast]: isEast,
          [classes.iconRowWest]: !isEast,
        })}
        isTopGrowing={isTopGrowing}
        isSurgeon={isSurgeon}
      >
        <CountRows
          className={classnames(
            classes.countRows,
            isEast ? classes.countRowsAlignEnd : classes.countRowsAlignStart,
          )}
          isEast={isEast}
          isSurgeon={isSurgeon}
          isTopGrowing={isTopGrowing}
        >
          {organRows.map(({ organType, shape, visibleDots }) => (
            <Box
              key={organType}
              className={getOrganClassName(organType, isEast)}
            >
              <Box className={classes.row}>
                {visibleDots.map((color, i) => (
                  <Box
                    key={`${staffInfo.id}-${organType}-${i}`}
                    className={classes[shape]}
                    sx={{
                      backgroundColor: color ?? 'transparent',
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </CountRows>

        <CircleIcon
          size={circleSize}
          color={avatarBackground}
          iconColor="#000000"
          icon={staffInfo.teamMemberType}
        />
      </IconRow>
    </Box>
  );
};
